import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Divider, FormControlLabel, FormGroup, Grid, IconButton, styled, Typography, Switch, switchClasses, Checkbox, Button } from "@mui/material";
import Header from "../../common/Header";
import Card from "../../common/Card";
import { useTheme } from "@mui/material/styles";
import { ColorModeContext, tokens } from "../../../theme";
import {AddCircleOutline, DeleteOutlineOutlined, LocationOn, ModeOutlined, PersonOutline, Save, VerifiedUserOutlined } from "@mui/icons-material";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { submitPracticeData, usePracticeRegistrationSlice, PracticeInfo, } from "../../../redux/slice/practice/practiceRegistrationSlice";
import PracticeLocationPopup from "./PracticeLocationPopup";
import ProviderPopup from "./ProviderPopup";
import { Controller, useForm } from "react-hook-form";
import CustomTextField from "../../common/CustomTextField";
import CustomNametField from "../../common/CustomNametField";
import CustomPatternTextField from "../../common/CustomPatternTextField";
import CustomRadioButton from "../../common/CustomRadioButton";
import { useCallback, useEffect, useMemo } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useCodetablesSlice, getCodeTableValuesByTableName, CodeTable, } from "../../../redux/slice/codetableSlice";
import { initialState } from "./PracticeLocationPopup";
import { providerInitialState } from "./ProviderPopup";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import { PracticeLocationInfo } from "../../../redux/slice/practice/practiceLocationSlice";
import { PracticeProviderInfo } from "../../../redux/slice/practice/practiceProvidersSlice";
import { fullName, getCategoryDisplayLabel } from "../../../utils/Utility";
import CustomCheckboxGroup from "../../common/CustomCheckboxGroup";
import { MainErrorInfo, setMainErrorObj, hideMainErrorPopup, SingleAlertInfo, setSingleAlertObj, } from "../../../redux/slice/commonSlice";
import LegacyToolBar from "../../common/LegacyToolBar";
import SignaturePad, { SignatureData, } from "../../common/signature/SignaturePad";
import { FAILED_CODE, SUCCESS_CODE } from "../../../service/ServiceConstants";
import AuthorizedSignerPopup, { AuthorizedSignerInfo } from "./AuthorizedSignerPopup";
import CustomIconButton from "../../common/CustomIconButton";
import CustomInfoMessage from "../../common/CustomInfoMessage";
import { useNavigation } from "../../../hooks/useNavigation";
import { RESPONSE_CODES, PRODUCT_CATEGORY, PRODUCT_CATEGORY_CODETABLE } from "../../../constants/applicationConstants";
import CustomNumberField from "../../common/CustomNumberField";
import { useBranding } from "../../../BrandingContext";
import CustomHelpMessage from "../../common/CustomHelpMessage";
import CustomStyledRadio from "../ivr/CustomStyledRadio";
import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import { useModalStyles } from "../../common/useModalStyles";

interface StepContent {
  [key: number]: JSX.Element;
}

interface Row {
  providerName: string;
  title: string;
  firstName: string;
  middleName: string;
  lastName: string;
  npi: string;
  ptan: string;
}

interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: "right" | "center" | "left";
  format?: (value: number) => string;
}

const preferenceOptions = [
  {
    value: "dateOfShipment",
    label: "Date of Shipment"
  },
  {
    value: "dateOfDelivery",
    label: "Date of Delivery"
  }
];

const CARE_AREA_DESCRIPTIONS: Record<string, string> = {
  [PRODUCT_CATEGORY.ADVANCED]: "Skin substitutes, cellular therapies, PRP/platelet-based products",
  [PRODUCT_CATEGORY.CONSERVATIVE]: "Collagen dressings, advanced wound care supplies",
  [PRODUCT_CATEGORY.STANDARD]: "Traditional wound care, compression, negative pressure",
  [PRODUCT_CATEGORY.GENERAL]: "GLP-1s, wellness, and other non-wound care products",
};

const PracticeRegistration: React.FC = () => {
  const { navigate } = useNavigation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const colorMode = React.useContext(ColorModeContext);
  const dispatch = useAppDispatch();
  const [currentStep, setCurrentStep] = React.useState(1);
  const totalSteps = 2;
  const { practiceRegistrationInfo } = usePracticeRegistrationSlice();
  const [showPracticesPopup, setShowPracticesPopup] = React.useState(false);
  const [showProviderPopup, setShowProviderPopup] = React.useState(false);
  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState,
    watch,
    unregister,
    clearErrors,
    trigger
  } = useForm<PracticeInfo>();
  const { errors } = formState;
  const [signatureData, setSignatureData] = React.useState("");
  const [provider, setProvider] = React.useState<PracticeProviderInfo[]>([]);
  // const recaptchaRef: React.RefObject<any> = React.createRef();
  const [disabled, setDisabled] = React.useState(false);
  const [showCaptcha, setShowCaptcha] = React.useState(false);
  // const [isRecaptchaValid, setIsRecaptchaValid] = React.useState(false);
  const [showCaptchaWarning, setShowCaptchaWarning] = React.useState(false);
  const { allCodeTablesValuesMap, allCodeTableValueFetchStatus } =
    useCodetablesSlice();
  const [productCategories, setProductCategories] = React.useState<CodeTable[]>([]);
  const [woundCareOptions, setWoundCareOptions] = React.useState<CodeTable[]>(
    []
  );
  const [placeOfService, setPlaceOfService] = React.useState<CodeTable[]>([]);
  const isPlaceOfServiceArray = Array.isArray(placeOfService);
  const [locationItemObject, setLocationItemObject] =
    React.useState<PracticeLocationInfo>(initialState);
  const [providerItemObject, setProviderItemObject] =
    React.useState<PracticeProviderInfo>(providerInitialState);
  const [locationIndex, setLocationIndex] = React.useState<
    number | undefined
  >();
  const [providerIndex, setProviderIndex] = React.useState<
    number | undefined
  >();
  const [showBillingAddressOption, setShowBillingAddressOption] =
    React.useState(false);
  const [insurancePayerList, setInsurancePayerList] = React.useState<
    CodeTable[]
  >([]);
  //CS-1242 - Start
  const [practiceSpecializationList, setPracticeSpecializationList] = React.useState<
    CodeTable[]
  >([]);
  //CS-1242 - End
  const formRef = React.useRef<HTMLFormElement>(null);
  // const [isBillerNotApplicable, setIsBillerNotApplicable] =
  //   React.useState(false);
  const [isWoundNotApplicable, setIsWoundNotApplicable] = React.useState(false);
  const [showCustomAlert, setShowCustomAlert] = React.useState(false);
  const [customAlertMessage, setCustomAlertMessage] = React.useState("");
  const [showAuthorizedSignerPopup, setShowAuthorizedSignerPopup] = React.useState(false);
  const [authorizedSignerData, setAuthorizedSignerData] = React.useState<AuthorizedSignerInfo>();
  const [officeContactChecked, setOfficeContactChecked] = React.useState<boolean>(false)
  const [accountInformationChecked, setAccountInformationChecked] = React.useState<boolean>(false)
  const defaultValues = useCallback(() => {
    if (practiceRegistrationInfo) {
      reset(practiceRegistrationInfo);
    }
  }, [practiceRegistrationInfo, reset]);

  const insurancePayerOptions = useMemo(() => {
    return insurancePayerList.map((item) => ({
      label: item.displayValue,
      value: item.keyName,
    }));
  }, [insurancePayerList]);
  const [dmeLicense, setDmeLicense] = React.useState<String | null>(null);
  const [billingCollagen, setBillingCollagen] = React.useState<String | null>(null);
  const [billMedicare, setBillMedicare] = React.useState<String | null>(null);
  const [billMedicareAdvantage, setBillMedicareAdvantage] = React.useState<String | null>(null);
  const [claimSupport, setClaimSupport] = React.useState<Boolean>(false);
  const isDark = theme.palette.mode === "dark";
  const [billerType, setBillerType] = React.useState<"primary" | "accountPayable" | "different">("different");
  const watchedCategories = watch("practiceRequestInfo.interestedProductCategory") || [];
  const [noRepChecked, setNoRepChecked] = React.useState<boolean>(false);
  const { footerSx, primaryBtnSx, cancelBtnSx } = useModalStyles();

  const productCategoryOptions = useMemo(() => {
    return productCategories.map(item => ({
      label: item.displayValue,
      value: item.keyName,
      description: CARE_AREA_DESCRIPTIONS[item.keyName] || "",
    }));
  }, [productCategories]);

  const allCareAreasSelected =
    productCategoryOptions.length > 0 &&
    productCategoryOptions.every((o) => watchedCategories.includes(o.value));

  const labelSx = {
    fontSize: "0.82rem",
    fontWeight: 600,
    color: isDark ? "#94a3b8" : "#64748b",
    mb: 0.6,
    display: "block",
  };

  const sectionTitleSx = {
    fontSize: "1rem",
    fontWeight: 700,
    color: isDark ? "#e2e8f0" : "#1e293b",
    mb: 1,
  };

  const radioSx = {
    color: isDark ? "#64748b" : "#94a3b8",
    "&.Mui-checked": { color: "#14b8a6" },
    padding: "4px 8px",
  };

  const checkboxSx = {
    color: isDark ? "#64748b" : "#94a3b8",
    "&.Mui-checked": { color: "#14b8a6" },
    padding: "4px 8px",
  };

  const dividerSx = {
    mb: 2.5,
    borderColor: isDark
      ? "rgba(255,255,255,0.07)"
      : "rgba(0,0,0,0.07)",
  };

  const addBtnSx = {
    textTransform: "none",
    fontSize: "0.82rem",
    fontWeight: 600,
    color: "#14b8a6",
    backgroundColor: "transparent",
    border: "1.5px solid rgba(20,184,166,0.5)",
    borderRadius: 2,
    px: 1.8,
    py: 0.5,
    "&:hover": {
      backgroundColor: "transparent",
      borderColor: "#14b8a6",
    },
  };

  const radioLabelSx = {
    fontSize: "0.85rem",
    color: isDark ? "#cbd5e1" : "#475569",
  };

  const branding = useBranding();

  const SwitchTextTrack = styled(Switch)({
    width: 74,
    height: 42,
    padding: 8,
    [`& .${switchClasses.switchBase}`]: {
      padding: 11,
      color: "#ff6a00",
    },
    [`& .${switchClasses.thumb}`]: {
      width: 20,
      height: 20,
      backgroundColor: "#fff",
    },
    [`& .${switchClasses.track}`]: {
      background:
        "linear-gradient(to right,rgb(161, 161, 161),rgb(84, 84, 84))",
      opacity: "1 !important",
      borderRadius: 20,
      position: "relative",
      "&:before, &:after": {
        display: "inline-block",
        position: "absolute",
        top: "50%",
        width: "50%",
        transform: "translateY(-50%)",
        color: "#fff",
        textAlign: "center",
        fontSize: "0.75rem",
        fontWeight: 500,
      },
      "&:before": {
        content: '"ON"',
        left: 4,
        opacity: 0,
      },
      "&:after": {
        content: '"OFF"',
        right: 4,
      },
    },
    [`& .${switchClasses.checked}`]: {
      [`&.${switchClasses.switchBase}`]: {
        color: "#185a9d",
        transform: "translateX(32px)",
        "&:hover": {
          backgroundColor: "rgba(24,90,257,0.08)",
        },
      },
      [`& .${switchClasses.thumb}`]: {
        backgroundColor: "#fff",
      },
      [`& + .${switchClasses.track}`]: {
        background: "linear-gradient(to right,rgb(0, 196, 88),rgb(0, 97, 29))",
        "&:before": {
          opacity: 1,
        },
        "&:after": {
          opacity: 0,
        },
      },
    },
  });

  // unmount
  useEffect(
    () => () => {
      dispatch(hideMainErrorPopup());
    },
    []
  );

  useEffect(() => {
    defaultValues();
  }, [practiceRegistrationInfo]);

  useEffect(() => {
    if (allCodeTableValueFetchStatus === "success" && allCodeTablesValuesMap) {
      setWoundCareOptions(
        getCodeTableValuesByTableName(allCodeTablesValuesMap, "WoundCare")
      );
      const sortedStateList = [
        ...getCodeTableValuesByTableName(allCodeTablesValuesMap, "PlaceOfService"),
      ].sort((a, b) => {
        const matchA = a.displayValue.match(/\((\d+)/);
        const numA: number | null = matchA ? parseInt(matchA[1], 10) : null;

        const matchB = b.displayValue.match(/\((\d+)/);
        const numB: number | null = matchB ? parseInt(matchB[1], 10) : null;

        if (numA === null && numB === null) return 0;
        if (numA === null) return 1;
        if (numB === null) return -1;
        return numA - numB;
      });

      setPlaceOfService(sortedStateList);
      setInsurancePayerList(
        getCodeTableValuesByTableName(allCodeTablesValuesMap, "InsurancePayer")
      );
      //CS-1242 - Start
      setPracticeSpecializationList(
        getCodeTableValuesByTableName(allCodeTablesValuesMap, "PracticeSpecializations")
      );
      //CS-1242 - End
      // CS-1289 START
      const sortedProductCategoryList = [
        ...getCodeTableValuesByTableName(allCodeTablesValuesMap, PRODUCT_CATEGORY_CODETABLE),
      ].sort((a, b) => a.displayValue.localeCompare(b.displayValue));
      setProductCategories(sortedProductCategoryList);
      // CS-1289 END
    }
  }, [allCodeTablesValuesMap, allCodeTableValueFetchStatus]);

  useEffect(() => {
    if (Object.keys(errors).length > 0 && currentStep === 1) {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [errors, currentStep]);
  //CS-561

  //CS-1334 - Start
  useEffect(() => {

    const contactFirstName = getValues("contactFirstName")
    const contactLastName = getValues("contactLastName")
    const contactMiddleName = getValues("contactMiddleName")
    const contactPhone = getValues("contactPhone")
    const contactEMail = getValues("contactEMail")

    if (officeContactChecked) {
      setValue("practiceRequestInfo.billerFirstName", contactFirstName)
      setValue("practiceRequestInfo.billerMiddleName", contactMiddleName)
      setValue("practiceRequestInfo.billerLastName", contactLastName)
      setValue("practiceRequestInfo.billerContactPhone", contactPhone)
      setValue("practiceRequestInfo.billerContactEmail", contactEMail)
    } else {
      setValue("practiceRequestInfo.billerFirstName", "")
      setValue("practiceRequestInfo.billerMiddleName", "")
      setValue("practiceRequestInfo.billerLastName", "")
      setValue("practiceRequestInfo.billerContactPhone", "")
      setValue("practiceRequestInfo.billerContactEmail", "")
    }
  }, [officeContactChecked])
  useEffect(() => {

    const apFirstName = getValues("apFirstName")
    const apLastName = getValues("apLastName")
    const apMiddleName = getValues("apMiddleName")
    const apPhone = getValues("apPhone")
    const apEmail = getValues("apEmail")

    if (accountInformationChecked) {
      setValue("practiceRequestInfo.billerFirstName", apFirstName)
      setValue("practiceRequestInfo.billerMiddleName", apMiddleName)
      setValue("practiceRequestInfo.billerLastName", apLastName)
      setValue("practiceRequestInfo.billerContactPhone", apPhone)
      setValue("practiceRequestInfo.billerContactEmail", apEmail)
    } else {
      setValue("practiceRequestInfo.billerFirstName", "")
      setValue("practiceRequestInfo.billerMiddleName", "")
      setValue("practiceRequestInfo.billerLastName", "")
      setValue("practiceRequestInfo.billerContactPhone", "")
      setValue("practiceRequestInfo.billerContactEmail", "")
    }
  }, [accountInformationChecked])
  //CS-1334 - End
  // CS-1289 START
  // const productCategoryOptions = useMemo(() => {
  //     return productCategories.map(item => ({
  //         label: item.displayValue,
  //         value: item.keyName
  //     }));
  // }, [productCategories]);

  /* istanbul ignore next -- never-used declaration (no call sites; flagged by no-unused-vars) */
  const mapDisplayValueToKeyName = (displayValue: string): string => {
    const category = productCategories.find(item => item.displayValue === displayValue);
    return category?.keyName || displayValue;
  };
  // CS-1289 END
  //
  const handleSignatureChange = (data: SignatureData) => {
    setSignatureData(data.signature);
    setValue("practiceRequestInfo.customerSignature", data.signature);
    setValue("practiceRequestInfo.customerName", data.typedText);
  };

  const handleChange = (name: string, value: any) => {
    switch (name) {
      case "dmeLicense":
        setDmeLicense(value);
        setValue("practiceRequestInfo.dmeLicense", value);
        if (value !== "true") {
          setBillingCollagen(null);
          setBillMedicare(null);
          setBillMedicareAdvantage(null);
          setValue("practiceRequestInfo.billingCollagen", null);
          setValue("practiceRequestInfo.billMedicare", null);
          setValue("practiceRequestInfo.billMedicareAdvantage", null);
          setValue("practiceRequestInfo.billCommercial", null);
        }
        break;
      case "billingCollagen":
        setBillingCollagen(value);
        setValue("practiceRequestInfo.billingCollagen", value);
        if (value !== "true") {
          setBillMedicare(null);
          setBillMedicareAdvantage(null);
          setValue("practiceRequestInfo.billMedicare", null);
          setValue("practiceRequestInfo.billMedicareAdvantage", null);
          setValue("practiceRequestInfo.billCommercial", null);
        }
        break;
      case "billMedicare":
        setBillMedicare(value);
        setValue("practiceRequestInfo.billMedicare", value);
        // if (value !== "true") {
        //   setBillMedicareAdvantage(null);
        //   setValue("practiceRequestInfo.billMedicareAdvantage", null);
        //   setValue("practiceRequestInfo.billCommercial", null);

        // }
        break;
      case "billMedicareAdvantage":
        setBillMedicareAdvantage(value);
        setValue("practiceRequestInfo.billMedicareAdvantage", value);
        // if (value !== "true") {
        //   setValue("practiceRequestInfo.billCommercial", null);
        // }
        break;
      default:
        break;
    }
  };

  const onSubmit = (data: PracticeInfo) => {
    // if (!isRecaptchaValid) {
    //   setShowCaptcha(true);
    //   setShowCaptchaWarning(true);
    //   return;
    // }

    if (data !== undefined) {
      data.practiceRequestInfo.customerName = "";
      data.practiceRequestInfo.customerSignature = "";
      handleSavePractice(data);
    }
    console.log("Practice Registrations:- ", data);

    // if (currentStep >= totalSteps && !isRecaptchaValid) {
    //   setShowCaptcha(true);
    //   setShowCaptchaWarning(true);
    //   return;
    // }

    // if (currentStep < totalSteps) {
    //   dispatch(hideMainErrorPopup());
    //   setCurrentStep(currentStep + 1);
    //   window.scrollTo(0, 0);
    // }

    // if (currentStep === totalSteps) {
    //   if (data !== undefined) {
    //     handleSavePractice(data);
    //   }
    // }
  };

  const onError = (_errors: any, e: any) => {
    const formContent = formRef.current;
    if (!formContent) return;

    const firstErrorEl = formContent.querySelector(
      'p.Mui-error, [aria-invalid="true"], .MuiFormHelperText-root.Mui-error'
    );

    if (firstErrorEl) {
      firstErrorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      formContent.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // const captchaOnChange = useCallback((response: any) => {
  //   try {
  //     if (response) {
  //       setIsRecaptchaValid(true);
  //       setShowCaptchaWarning(false);
  //       setDisabled(false);
  //     } else {
  //       setIsRecaptchaValid(false);
  //     }
  //   } catch (error) {
  //     console.log(error);
  //   }
  // }, []);

  const handleSavePractice = async (practiceInfoData: PracticeInfo) => {
    // let errorObj: MainErrorInfo = {
    //   title: "",
    // };

    try {
      const response = await dispatch(submitPracticeData({ practiceData: practiceInfoData, interceptorOption: { skipErrorPopup: true } }));

      if (response.payload && response.payload.message === SUCCESS_CODE) {
        navigate("/success");

        const alertObj: SingleAlertInfo = {
          message: "Practice Registration Submitted Successfully",
          alertType: "success",
        };

        dispatch(setSingleAlertObj(alertObj));

        // } else {
        //   let errorSentence = '';
        //   if ((response.payload.data.code) === FAILED_CODE) {
        //     errorSentence = response.payload.data.message || "An unknown error occurred.";
        //   } else {
        //     response.payload.errors.forEach((error: any, index: any) => {
        //       if (error.field) {
        //         errorSentence += `${error.field}: ${error.errorMessage}\n`;
        //       } else {
        //         errorSentence += `${error.reason}\n`;
        //       }
        //     });
        //   }

        //   errorObj.title = "Submit Practice Failed!";
        //   errorObj.errorMessageStream = errorSentence;
        //   dispatch(setMainErrorObj(errorObj));
      } else if (response?.payload?.code === RESPONSE_CODES.FAILED_CODE) {
        let alertMessage = response.payload?.message || "An unknown error occurred.";
        console.log("Error submitting practice data:", response.payload);

        // if (response.payload?.errors) {
        //   alertMessage = response.payload.errors
        //     .map((error: any) =>
        //       error.field ? `${error.field}: ${error.errorMessage}` : error.reason
        //     )
        //     .join("\n");
        // }

        const alertObj: SingleAlertInfo = {
          message: alertMessage,
          alertType: "error",
        };

        dispatch(setSingleAlertObj(alertObj));
        onError(null, null);
      }
    } catch (error) {
      console.log("Error submitting practice data:", error);
      // errorObj.title = "Error submitting practice data";
      // errorObj.errorMessageStream = "" + error;
      // dispatch(setMainErrorObj(errorObj));
    }
  };

  // const handleBackClick = () => {
  //   if (currentStep > 1) {
  //     setCurrentStep(currentStep - 1);
  //     window.scrollTo(0, 0);
  //   }
  // };

  const clickShowPracticesPopup = () => {
    setShowPracticesPopup(true);
    if (
      getValues("practiceLocations") === undefined ||
      getValues("practiceLocations").length == 0 ||
      locationIndex == 0
    ) {
      setShowBillingAddressOption(true);
    }
  };

  const clickShowAuthorizedSignerPopup = () => {
    setShowAuthorizedSignerPopup(true);
  };

  const clickCloseAuthorizedSignerPopup = () => {
    setShowAuthorizedSignerPopup(false);
    setAuthorizedSignerData(undefined)

  };

  const clickClosePracticesPopup = () => {
    setShowPracticesPopup(false);
    setLocationItemObject(initialState);
    setLocationIndex(undefined);
    setShowBillingAddressOption(false);
  };

  const clickShowProviderPopup = () => {
    setShowProviderPopup(true);
  };

  const clickCloseProviderPopup = () => {
    setShowProviderPopup(false);
    setProviderItemObject(providerInitialState);
    setProviderIndex(undefined);
  };

  // Add practice start
  const columnsPractice: Column[] = [
    { id: "streetAddress", label: "Street Address", minWidth: 200 },
    { id: "unitNumber", label: "Unit Number", minWidth: 100 },
    { id: "city", label: "City", minWidth: 100 },
    { id: "state", label: "State ", minWidth: 100 },
    { id: "zipCode", label: "Zip", minWidth: 100 },
    { id: "isBillingAddress", label: "Billing Address", minWidth: 100 },
    { id: "action", label: "Action", minWidth: 100 }, //CS-1403 - removed alignment property
  ];

  const addPractice = (practiceLocation: PracticeLocationInfo) => {
    let practiceLocations = getValues("practiceLocations") || [];

    if (practiceLocation.isBillingAddress === "yes") {
      const hasBillingAddress = practiceLocations.some(
        (location) => location.isBillingAddress === "yes"
      );
    }

    if (locationIndex !== undefined) {
      const oldLocation = practiceLocations[locationIndex];
      const updatedLocation = { ...oldLocation, ...practiceLocation };
      updatePracticeLocation(locationIndex, updatedLocation);
    } else {
      practiceLocations = [...practiceLocations, practiceLocation];
      setValue("practiceLocations", practiceLocations);
    }
    setShowBillingAddressOption(
      practiceLocations.length > 0 &&
      !practiceLocations.some(
        (location) => location.isBillingAddress === "yes"
      )
    );
  };

  // Add practice end

  // Update practice location
  const updatePracticeLocation = (
    index: number,
    updatedLocation: PracticeLocationInfo
  ) => {
    let updatedLocations = getValues("practiceLocations");
    updatedLocations[index] = updatedLocation;
    setValue("practiceLocations", updatedLocations);
  };

  const transformPracticeLocations = (
    locations: { isBillingAddress: any }[]
  ) => {
    return locations.map((location: { isBillingAddress: any }) => ({
      ...location,
      isBillingAddress:
        location.isBillingAddress.toLowerCase() === "yes" ? "Yes" : "No",
    }));
  };

  // Add provider start
  const columnsProvider: Column[] = [
    { id: "providerName", label: "Provider Name", minWidth: 200 },
    { id: "npi", label: "Provider NPI", minWidth: 100 },
    { id: "ptan", label: "Provider PTAN", minWidth: 100 },
    { id: "taxId", label: "Provider Tax ID", minWidth: 100 },
    { id: "email", label: "Provider Email", minWidth: 200 },
    { id: "action", label: "Action", minWidth: 100 }, //CS-1403 - removed alignment property
  ];

  const handleProviderChange = (name: string, value: string) => {
    setProvider((prevPro) => ({
      ...prevPro,
      [name]: value,
    }));
  };

  const addProvider = (provider: PracticeProviderInfo) => {
    let practiceProviders = getValues("practiceProviders");
    if (providerIndex !== undefined) {
      const oldProvider = practiceProviders[providerIndex];
      const updatedProvider = { ...oldProvider, ...provider };
      updatePracticeProvider(providerIndex, updatedProvider);
    } else {
      if (practiceProviders)
        setValue("practiceProviders", [
          ...getValues("practiceProviders"),
          provider,
        ]);
      else setValue("practiceProviders", [provider]);
    }
  };
  // Add provider end

  const columnsSigner: Column[] = [
    { id: "fullTitleName", label: "Full Name", minWidth: 200 },
    { id: "email", label: "Email", minWidth: 100 },
    { id: "isOwner", label: "Ownership", minWidth: 100 },
    { id: "position", label: "Position", minWidth: 100 },
    { id: "action", label: "Action", minWidth: 100 },
  ];

  const addSigner = (signer: AuthorizedSignerInfo) => {
    let authorizedSigners = getValues("practiceRequestInfo.authorizedSigners");
    console.log(signer.index)
    if (signer.index || signer.index === 0) {
      authorizedSigners[signer.index] = signer;
      setValue("practiceRequestInfo.authorizedSigners", authorizedSigners);
    }
    else if (authorizedSigners)
      setValue("practiceRequestInfo.authorizedSigners", [
        ...getValues("practiceRequestInfo.authorizedSigners"),
        signer,
      ]);
    else setValue("practiceRequestInfo.authorizedSigners", [signer]);
  };

  // Update practice provider
  const updatePracticeProvider = (
    index: number,
    updatedProvider: PracticeProviderInfo
  ) => {
    let updatedProviders = getValues("practiceProviders");
    updatedProviders[index] = updatedProvider;
    setValue("practiceProviders", updatedProviders);
  };

  const handleEditActionLocation = (row: any, rowIndex: number) => {
    if (rowIndex === 0) {
      setShowBillingAddressOption(true);
    } else {
      setShowBillingAddressOption(false);
    }
    const editedRow = {
      ...row,
      isBillingAddress: row.isBillingAddress === "Yes" ? "yes" : "no",
    };
    setLocationIndex(rowIndex);
    setLocationItemObject(editedRow);
    clickShowPracticesPopup();
  };

  const handleDeleteActionLocation = (row: any, rowIndex: number) => {
    let addedPracticeLocations = getValues("practiceLocations");
    if (rowIndex >= 0 && rowIndex < addedPracticeLocations.length) {
      addedPracticeLocations.splice(rowIndex, 1);
      setValue("practiceLocations", addedPracticeLocations);
    } else {
      console.error("Invalid index");
    }
    if (getValues("practiceLocations").length === 0) {
      setShowBillingAddressOption(true);
    }
    setLocationIndex(rowIndex);
  };

  const handleEditActionProvider = (row: any, rowIndex: number) => {
    setProviderIndex(rowIndex);
    setProviderItemObject(row);
    clickShowProviderPopup();
  };

  const handleDeleteActionProvider = (row: any, rowIndex: number) => {
    let addedPracticeProviders = formattedProvidersInfo;
    if (rowIndex >= 0 && rowIndex < addedPracticeProviders.length) {
      addedPracticeProviders.splice(rowIndex, 1);
      // @ts-ignore
      setValue("practiceProviders", addedPracticeProviders);
    } else {
      console.error("Invalid index");
    }
  };

  //CS-1425 START
  const formatAuthorizedSigners = (signers: AuthorizedSignerInfo[] | undefined) => {
    if (!signers) return [];
    return signers.map((signer) => ({
      ...signer,
      fullTitleName: signer.title ? `${signer.title} ${signer.fullName}` : signer.fullName
    }));
  };
  //CS-1425 END

  const handleRowClick = (row: any) => { };

  const legacyItems = [
    { icon: <ModeOutlined color="info" />, text: "Edit" },
    { icon: <DeleteOutlineOutlined color="error" />, text: "Delete" },
  ];

  const validatePracticeLocations = () => {
    let practiceLocations = getValues("practiceLocations");
    const hasBillingAddress = practiceLocations.some(
      (location) => location.isBillingAddress === "yes"
    );
    if (hasBillingAddress) {
      return true;
    } else {
      return "At least one practice location must be marked as a billing address.";
    }
  };
  //modify the practice providers object array to display full name
  const formattedProvidersInfo: Row[] = getValues("practiceProviders")
    ? getValues("practiceProviders").map((practice: any) => ({
      providerName: fullName(
        practice.title,
        practice.firstName,
        practice.middleName,
        practice.lastName
      ),
      title: practice.title,
      firstName: practice.firstName,
      middleName: practice.middleName,
      lastName: practice.lastName,
      npi: practice.npi,
      ptan: practice.ptan,
      taxId: practice.taxId,
      email: practice.email,
    }))
    : [];

  // useEffect(() => {
  //   if (isBillerNotApplicable) {
  //     setValue("practiceRequestInfo.billerStreetAddress", "", {
  //       shouldValidate: false,
  //     });
  //     setValue("practiceRequestInfo.billerUnitNumber", "", {
  //       shouldValidate: false,
  //     });
  //     setValue("practiceRequestInfo.billerCity", "", { shouldValidate: false });
  //     setValue("practiceRequestInfo.billerState", "", {
  //       shouldValidate: false,
  //     });
  //     setValue("practiceRequestInfo.billerZipCode", "", {
  //       shouldValidate: false,
  //     });
  //   }
  // }, [isBillerNotApplicable, setValue]);

  useEffect(() => {
    if (isWoundNotApplicable) {
      setValue("practiceRequestInfo.averageWoundsTreatedPerMonth", "", {
        shouldValidate: false,
      });
      setValue("practiceRequestInfo.woundCareFocus", '', {
        shouldValidate: false,
      });
    }
  }, [isWoundNotApplicable, setValue]);

  // const handleBillerNotApplicableChange = (event: any) => {
  //   setIsBillerNotApplicable(event.target.checked);
  // };

  /* istanbul ignore next -- never-used declaration (no call sites; flagged by no-unused-vars) */
  const handleWoundNotApplicableChange = (event: any) => {
    setIsWoundNotApplicable(event.target.checked);
  };

  const getErrorLocationMessage = (field: any, fieldState: any, hasPracticeLocations: any) => {

    if (!hasPracticeLocations) {
      return "At least 1 practice location is required.";
    } else {
      let practiceLocations = getValues("practiceLocations");
      const hasBillingAddress = practiceLocations.some(
        (location) => location.isBillingAddress === "yes"
      );
      if (hasBillingAddress) {
        return "";
      } else {
        return "At least one practice location must be marked as a billing address.";
      }
    }
  }

  const getErrorAuthorizedSignerMessage = (field: any, fieldState: any, hasAuthorizedSigners: any) => {

    if (!hasAuthorizedSigners) {
      return "At least 1 Authorized Signer is required.";
    } else {
      return "";
    }
  }

  // useEffect(() => {
  //   const claimSupported = watch('isClaimSupported') ?? false;
  //   setClaimSupport(claimSupported);
  // }, [watch('isClaimSupported')]);


  const signaturePadValidations = {
    name: {
      required: "Name is required.",
    },
    signature: {
      required: "Signature is required.",
    },
  };

  const handleProductCategoryChange = () => {
    const selected = getValues("practiceRequestInfo.interestedProductCategory");
    // CS-1760 - Compare against raw keynames
    if ((selected || []).includes(PRODUCT_CATEGORY.CONSERVATIVE)) {
      setValue("dosPreference", "dateOfShipment");
    } else {
      setValue("dosPreference", "");
    }
    trigger("practiceRequestInfo.interestedProductCategory");
  }

  const handleSignerChange = (row: AuthorizedSignerInfo, rowIndex: number) => {
    let obj: AuthorizedSignerInfo = {
      ...row,
      index: rowIndex,
    }
    setShowAuthorizedSignerPopup(true);
    setAuthorizedSignerData(obj);
  };

  const handleDeleteAuthorizedSigner = (row: AuthorizedSignerInfo, rowIndex: number) => {
    let addedAuthorizedSigners = getValues("practiceRequestInfo.authorizedSigners");
    if (rowIndex >= 0 && rowIndex < addedAuthorizedSigners.length) {
      addedAuthorizedSigners.splice(rowIndex, 1);
      setValue("practiceRequestInfo.authorizedSigners", addedAuthorizedSigners);
    }
  }

  const handleCancel = () => {
    navigate("/dashboard")
  };

  const stepContent: StepContent = {
    1: (
      <div>
        <Header
          title="Practice Information"
          message="Enter N/A if information is not available or applicable."
        />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Typography component="label" sx={labelSx}>Practice Name *</Typography>
            <CustomTextField control={control} controllerName="practiceName"
              placeholder="Enter practice name"
              rules={{
                required: "Practice Name is required.",
                validate: {
                  customValidation: (value: string) => {
                    if (value?.trim() === "") {
                      return "Practice Name is required.";
                    }
                  }
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography component="label" sx={labelSx}>Practice Tax ID *</Typography>
            <CustomTextField control={control} controllerName="taxId" placeholder="XX-XXXXXXX" maxLength={10}
              rules={{
                required: "Practice Tax ID is required.",
                validate: {
                  doesNotContainSpaces: (value: string) =>
                    !/\s/.test(value) || `Tax ID cannot contain spaces.`,
                  customValidation: (value: string) => {
                    if (value?.trim() === "") {
                      return "Practice Tax ID is required.";
                    }
                  }
                }
              }}
            />
          </Grid>
          <Grid item md={4}></Grid>
          <Grid item xs={12} sm={4}>
            <Typography component="label" sx={labelSx}>Practice NPI *</Typography>
            <CustomTextField control={control} controllerName="npi" placeholder="10-digit NPI" maxLength={10}
              rules={{
                required: "NPI is required.",
                validate: {
                  doesNotContainSpaces: (value: string) =>
                    !/\s/.test(value) || `NPI cannot contain spaces.`,
                  customValidation: (value: string) => {
                    if (value?.trim() === "") {
                      return "NPI is required.";
                    }
                  }
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography component="label" sx={labelSx}>Practice PTAN *</Typography>
            <CustomTextField control={control} controllerName="ptan" placeholder="Enter PTAN" maxLength={10}
              rules={{
                required: "PTAN is required.",
                validate: {
                  doesNotContainSpaces: (value: string) =>
                    !/\s/.test(value) || `PTAN cannot contain spaces.`,
                  customValidation: (value: string) => {
                    if (value?.trim() === "") {
                      return "PTAN is required.";
                    }
                  }
                }
              }}
            />
          </Grid>
          <Grid item md={4}></Grid>
          <Grid item xs={12} sm={12}>
            <Typography component="label" sx={labelSx}>Contact Name *</Typography>
            <CustomNametField control={control} context="Contact" xs={12} md={4} isRequired={true} usePlaceholder={true} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography component="label" sx={labelSx}>Contact Email *</Typography>
            <CustomTextField control={control} controllerName="contactEmail" placeholder="email@practice.com"
              rules={{
                required: "Contact Email is required.",
                pattern: {
                  value: /^([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}|n\/a|N\/A)$/i,
                  message: "Invalid email.",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography component="label" sx={labelSx}>Contact Phone *</Typography>
            <CustomPatternTextField control={control} placeholder="(555) 555-5555" name="contactPhone"
              rules={{
                required: "Contact Phone is required.",
                pattern: {
                  value: /^(\+\d{1,2}\s)?\(\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/,
                  message: "Invalid phone number.",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography component="label" sx={labelSx}>Contact Fax</Typography>
            <CustomPatternTextField control={control} placeholder="(555) 555-5555" name="contactFax"
              rules={{
                pattern: {
                  value: /^(\+\d{1,2}\s)?\(\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/,
                  message: "Invalid fax number.",
                },
              }}
            />
          </Grid>
          <Grid item md={12}>
            <Divider orientation="horizontal" variant="middle" flexItem sx={{ padding: 0, margin: 0 }} />
          </Grid>
          <Grid item md={12}>
            <Box mb={1.5}>
              <Typography component="label" sx={labelSx}>How do you bill? *</Typography>
              <Grid item>
                <CustomRadioButton control={control} controllerName="billingType"
                  radioSx={radioSx}
                  radioLabelSx={radioLabelSx}
                  options={[
                    { value: "INDIVIDUAL", label: "Individual NPI" },
                    { value: "GROUP", label: "Group NPI" },
                  ]}
                  rules={{ required: "Billing type is required." }}
                />
              </Grid>
            </Box>
          </Grid>

          <Grid item xs={12} md={12}>
            <Divider orientation="horizontal" variant="middle" flexItem sx={{ padding: 0, margin: 0 }} />
          </Grid>

          <Grid item md={12}>

            <Box mb={1}>
              <Typography component="label" sx={labelSx}>
                Do you have a DME license? *
              </Typography>
              <CustomRadioButton
                control={control}
                controllerName="practiceRequestInfo.dmeLicense"
                radioSx={radioSx}
                radioLabelSx={radioLabelSx}
                options={[
                  { value: true, label: "Yes" },
                  { value: false, label: "No" },
                ]}
                rules={{ required: "Please select the option" }}
                onChange={(e) => handleChange("dmeLicense", e.target.value)}
              />

              {dmeLicense === "true" && (
                <Grid item xs={12} sm={4}>
                  <Typography component="label" sx={labelSx}>
                    Will you be doing your own DME billing? *
                  </Typography>
                  <CustomRadioButton
                    control={control}
                    controllerName="practiceRequestInfo.billingCollagen"
                    options={[
                      { value: true, label: "Yes" },
                      { value: false, label: "No" },
                    ]}
                    rules={{ required: "Please select the option" }}
                    onChange={(e) =>
                      handleChange("billingCollagen", e.target.value)
                    }
                    radioSx={radioSx}
                    radioLabelSx={radioLabelSx}
                  />
                </Grid>
              )}
              {dmeLicense === "true" && billingCollagen === "true" && (
                <Grid item xs={12} sm={4}>
                  <Typography component="label" sx={labelSx}>
                    Will you bill Medicare?  *
                  </Typography>
                  <CustomRadioButton
                    control={control}
                    controllerName="practiceRequestInfo.billMedicare"
                    options={[
                      { value: true, label: "Yes" },
                      { value: false, label: "No" },
                    ]}
                    rules={{ required: "Please select the option" }}
                    onChange={(e) =>
                      handleChange("billMedicare", e.target.value)
                    }
                    radioSx={radioSx}
                    radioLabelSx={radioLabelSx}
                  />
                </Grid>
              )}
              {dmeLicense === "true" && billingCollagen === "true" && (
                <Grid item xs={12} sm={4}>
                  <Typography component="label" sx={labelSx}>
                    Will you bill Medicare Advantage? *
                  </Typography>
                  <CustomRadioButton
                    control={control}
                    controllerName="practiceRequestInfo.billMedicareAdvantage"
                    options={[
                      { value: true, label: "Yes" },
                      { value: false, label: "No" },
                    ]}
                    rules={{ required: "Please select the option" }}
                    onChange={(e) =>
                      handleChange("billMedicareAdvantage", e.target.value)
                    }
                    radioSx={radioSx}
                    radioLabelSx={radioLabelSx}
                  />
                </Grid>
              )}
              {dmeLicense === "true" && billingCollagen === "true" && (
                <Grid item xs={12} sm={4}>
                  <Typography component="label" sx={labelSx}>
                    Will you bill Commercial? *
                  </Typography>
                  <CustomRadioButton
                    control={control}
                    controllerName="practiceRequestInfo.billCommercial"
                    options={[
                      { value: true, label: "Yes" },
                      { value: false, label: "No" },
                    ]}
                    rules={{ required: "Please select the option" }}
                    radioSx={radioSx}
                    radioLabelSx={radioLabelSx}
                  />
                </Grid>
              )}

            </Box>
          </Grid>

          <Grid item md={12}>

            <Controller
              name="practiceLocations"
              control={control}
              rules={{
                required: "At least 1 practice location is required.",
                validate: { validatePracticeLocations },
              }}
              render={({ field, fieldState }) => {
                const hasPracticeLocations = getValues("practiceLocations")?.length > 0;
                return (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start", 
                        gap: 5,
                        mb: 1,
                      }}
                    >
                      <Typography sx={sectionTitleSx}>Practice Locations *</Typography>
                      <Button
                        startIcon={<AddIcon />}
                        size="small"
                        sx={addBtnSx}
                        onClick={clickShowPracticesPopup}
                      >
                        Add Location
                      </Button>
                    </Box>
                    <Divider sx={dividerSx} />

                    {fieldState.error && (
                      <Typography
                        color="error"
                        variant="body2"
                        style={{ marginTop: "8px" }}
                      >
                        {getErrorLocationMessage(field, fieldState, hasPracticeLocations)}
                      </Typography>
                    )}

                    {hasPracticeLocations ? (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 3 }}>
                        {transformPracticeLocations(getValues("practiceLocations")).map((location: any, rowIndex: number) => {
                          const addressLine = [location.city, location.state, location.zipCode]
                            .filter(Boolean)
                            .join(", ");
                          const isBilling = location.isBillingAddress === "Yes";
                          return (
                            <Box
                              key={rowIndex}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                px: 2,
                                py: 1.5,
                                borderRadius: 2,
                                border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
                                backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
                                transition: "border-color 0.15s",
                                "&:hover": {
                                  borderColor: isDark ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.16)",
                                },
                              }}
                            >
                              {/* Left: icon + text */}
                              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                                <LocationOn
                                  sx={{
                                    mt: 0.2,
                                    fontSize: "1.2rem",
                                    color: isDark ? "#475569" : "#94a3b8",
                                    flexShrink: 0,
                                  }}
                                />
                                <Box>
                                  <Typography
                                    sx={{
                                      fontSize: "0.88rem",
                                      fontWeight: 600,
                                      color: isDark ? "#e2e8f0" : "#1e293b",
                                      lineHeight: 1.4,
                                    }}
                                  >
                                    {location.streetAddress}
                                    {location.unitNumber ? `, ${location.unitNumber}` : ""}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: "0.78rem",
                                      color: isDark ? "#64748b" : "#94a3b8",
                                      mt: 0.2,
                                    }}
                                  >
                                    {addressLine}
                                    {isBilling && (
                                      <Box
                                        component="span"
                                        sx={{ color: isDark ? "#94a3b8" : "#64748b" }}
                                      >
                                        {" • Billing Address"}
                                      </Box>
                                    )}
                                  </Typography>
                                </Box>
                              </Box>

                              {/* Right: Edit text btn + delete icon */}
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => handleEditActionLocation(location, rowIndex)}
                                  sx={{
                                    textTransform: "none",
                                    fontSize: "0.82rem",
                                    fontWeight: 600,
                                    color: isDark ? "#cbd5e1" : "#475569",
                                    minWidth: "auto",
                                    px: 1,
                                    py: 0.5,
                                    "&:hover": {
                                      backgroundColor: "transparent",
                                      color: "#14b8a6",
                                    },
                                  }}
                                >
                                  Edit
                                </Button>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteActionLocation(location, rowIndex)}
                                  sx={{
                                    color: "#ef4444",
                                    p: 0.5,
                                    "&:hover": {
                                      backgroundColor: "rgba(239,68,68,0.08)",
                                    },
                                  }}
                                >
                                  <DeleteOutlineOutlined fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    ) : (
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          color: isDark ? "#64748b" : "#94a3b8",
                          mb: 3,
                        }}
                      >
                        No practice locations added yet.
                      </Typography>
                    )}
                  </>
                );
              }}
            />

          </Grid>

          <Grid item md={12}>
            <Controller
              name="practiceProviders"
              control={control}
              rules={{ required: "At least 1 provider is required." }}
              render={({ field, fieldState }) => {
                const hasPracticeProviders = getValues("practiceProviders")?.length > 0;
                return (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start", 
                        gap: 5,
                        mb: 1,
                      }}
                    >
                      <Typography sx={sectionTitleSx}>Providers *</Typography>
                      <Button
                        startIcon={<AddIcon />}
                        size="small"
                        sx={addBtnSx}
                        onClick={clickShowProviderPopup}
                      >
                        Add Provider
                      </Button>
                    </Box>
                    <Divider sx={dividerSx} />

                    {fieldState.error && !hasPracticeProviders && (
                      <Typography
                        color="error"
                        variant="body2"
                        style={{ marginTop: "8px" }}
                      >
                        {fieldState.error.message}
                      </Typography>
                    )}

                    {hasPracticeProviders && (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 3 }}>
                        {formattedProvidersInfo.map((provider: any, rowIndex: number) => (
                          <Box
                            key={rowIndex}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              px: 2,
                              py: 1.5,
                              borderRadius: 2,
                              border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
                              backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
                              transition: "border-color 0.15s",
                              "&:hover": {
                                borderColor: isDark ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.16)",
                              },
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                              <PersonOutline
                                sx={{
                                  mt: 0.2,
                                  fontSize: "1.2rem",
                                  color: isDark ? "#475569" : "#94a3b8",
                                  flexShrink: 0,
                                }}
                              />
                              <Box>
                                <Typography
                                  sx={{
                                    fontSize: "0.88rem",
                                    fontWeight: 600,
                                    color: isDark ? "#e2e8f0" : "#1e293b",
                                    lineHeight: 1.4,
                                  }}
                                >
                                  {provider.providerName}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "0.78rem",
                                    color: isDark ? "#64748b" : "#94a3b8",
                                    mt: 0.2,
                                  }}
                                >
                                  {[
                                    provider.npi && `NPI: ${provider.npi}`,
                                    provider.ptan && `PTAN: ${provider.ptan}`,
                                  ]
                                    .filter(Boolean)
                                    .join(" • ")}
                                </Typography>
                              </Box>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => handleEditActionProvider(provider, rowIndex)}
                                sx={{
                                  textTransform: "none",
                                  fontSize: "0.82rem",
                                  fontWeight: 600,
                                  color: isDark ? "#cbd5e1" : "#475569",
                                  minWidth: "auto",
                                  px: 1,
                                  py: 0.5,
                                  "&:hover": {
                                    backgroundColor: "transparent",
                                    color: "#14b8a6",
                                  },
                                }}
                              >
                                Edit
                              </Button>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteActionProvider(provider, rowIndex)}
                                sx={{
                                  color: "#ef4444",
                                  p: 0.5,
                                  "&:hover": {
                                    backgroundColor: "rgba(239,68,68,0.08)",
                                  },
                                }}
                              >
                                <DeleteOutlineOutlined fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {!hasPracticeProviders && (
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          color: isDark ? "#64748b" : "#94a3b8",
                          mb: 3,
                        }}
                      >
                        No providers added yet.
                      </Typography>
                    )}
                  </>
                );
              }}
            />
          </Grid>
          <Grid item md={12}>
            <Divider orientation="horizontal" variant="middle" flexItem sx={{ padding: 0, margin: 0 }} />
          </Grid>
        </Grid>

        <div style={{ height: "30px" }}></div>
        {/* ── Account Payable Information ── */}
        <Typography sx={sectionTitleSx}>Account Payable Information</Typography>
        <Divider sx={dividerSx} />

        <Grid container spacing={2.5} mb={3}>
          <Grid item xs={12} sm={12}>
            <Typography component="label" sx={labelSx}>AP Contact Name *</Typography>
            <CustomNametField control={control} context="AP" xs={12} md={4} usePlaceholder={true} isRequired={true} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography component="label" sx={labelSx}>AP Contact Phone *</Typography>
            <CustomPatternTextField control={control} name={"apPhone"} placeholder="(555) 555-5555"
              rules={{
                required: "AP Contact Phone is required.",
                pattern: {
                  value: /^(\+\d{1,2}\s)?\(\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/,
                  message: "Invalid phone number.",
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography component="label" sx={labelSx}>AP Contact Email *</Typography>
            <CustomTextField control={control} controllerName={"apEmail"} placeholder="ap@practice.com"
              rules={{
                required: "AP Contact Email is required.",
                pattern: {
                  value: /^([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}|n\/a|N\/A)$/i,
                  message: "Invalid email.",
                },
              }}
            />
          </Grid>
        </Grid>

        <div style={{ height: "30px" }}></div>
        <Typography sx={sectionTitleSx}>Biller Information</Typography>
        <Divider sx={{ ...dividerSx, mb: 2 }} />
        <Grid item sx={{ mb: 2 }}>
          <CustomRadioButton control={control} controllerName="billerSelection"
            radioSx={radioSx}
            radioLabelSx={radioLabelSx}
            options={[
              { value: "primary", label: "Same as Primary Contact" },
              { value: "accountPayable", label: "Same as Account Payable" },
              { value: "different", label: "Different Contact" },
            ]}
            rules={{ required: "Biller Information is required." }}
            onChange={(e) => {
              const value = e.target.value as "primary" | "accountPayable" | "different";
              setBillerType(value);
              setOfficeContactChecked(value === "primary");
              setAccountInformationChecked(value === "accountPayable");
            }}
          />
        </Grid>

        {billerType === "different" && (
          <Grid container spacing={2.5} mb={3}>
            <Grid item xs={12} sm={12}>
              <Typography component="label" sx={labelSx}>Biller Name *</Typography>
              <CustomNametField control={control} context="biller" name={"practiceRequestInfo"}
                label="Biller Contact" xs={12} md={4} usePlaceholder={true} isRequired={true}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography component="label" sx={labelSx}>Biller Phone *</Typography>
              <CustomPatternTextField control={control} placeholder="(555) 555-5555"
                name={`practiceRequestInfo.${"billerContactPhone"}`}
                rules={{
                  required: "Biller Contact Phone is required.",
                  pattern: {
                    value: /^(\+\d{1,2}\s)?\(\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}$/,
                    message: "Invalid phone number.",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography component="label" sx={labelSx}>Biller Email *</Typography>
              <CustomTextField control={control} controllerName={`practiceRequestInfo.${"billerContactEmail"}`}
                placeholder="biller@practice.com"
                rules={{
                  required: "Biller Contact Email is required.",
                  pattern: {
                    value: /^([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}|n\/a|N\/A)$/i,
                    message: "Invalid email.",
                  },
                }}
              />
            </Grid>
          </Grid>
        )}

        <Grid item md={12}>
          <Controller
            name={`practiceRequestInfo.${"authorizedSigners"}`}
            control={control}
            rules={{
              required: "At least 1 Authorized Signer is required.",
            }}
            render={({ field, fieldState }) => {
              const hasAuthorizedSigners =
                getValues("practiceRequestInfo.authorizedSigners")?.length > 0;
              return (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      gap: 5,
                      mb: 1,
                    }}
                  >
                    <Typography sx={sectionTitleSx}>Authorized Signer(s) *</Typography>
                    <Button
                      startIcon={<AddIcon />}
                      size="small"
                      sx={addBtnSx}
                      onClick={clickShowAuthorizedSignerPopup}
                    >
                      Add Authorized Signer
                    </Button>
                  </Box>
                  <Divider sx={dividerSx} />

                  {fieldState.error && (
                    <Typography
                      color="error"
                      variant="body2"
                      style={{ marginTop: "8px" }}
                    >
                      {getErrorAuthorizedSignerMessage(field, fieldState, hasAuthorizedSigners)}
                    </Typography>
                  )}

                  {hasAuthorizedSigners && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 3 }}>
                      {formatAuthorizedSigners(getValues("practiceRequestInfo.authorizedSigners")).map((signer: any, rowIndex: number) => (
                        <Box
                          key={rowIndex}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            px: 2,
                            py: 1.5,
                            borderRadius: 2,
                            border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
                            backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
                            transition: "border-color 0.15s",
                            "&:hover": {
                              borderColor: isDark ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.16)",
                            },
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                            <VerifiedUserOutlined
                              sx={{
                                mt: 0.2,
                                fontSize: "1.2rem",
                                color: isDark ? "#475569" : "#94a3b8",
                                flexShrink: 0,
                              }}
                            />
                            <Box>
                              <Typography
                                sx={{
                                  fontSize: "0.88rem",
                                  fontWeight: 600,
                                  color: isDark ? "#e2e8f0" : "#1e293b",
                                  lineHeight: 1.4,
                                }}
                              >
                                {signer.fullTitleName}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "0.78rem",
                                  color: isDark ? "#64748b" : "#94a3b8",
                                  mt: 0.2,
                                }}
                              >
                                {[
                                  signer.email,
                                  signer.position && `Position: ${signer.position}`,
                                  signer.isOwner && `Ownership: ${signer.isOwner}`,
                                ]
                                  .filter(Boolean)
                                  .join(" • ")}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => handleSignerChange(signer, rowIndex)}
                              sx={{
                                textTransform: "none",
                                fontSize: "0.82rem",
                                fontWeight: 600,
                                color: isDark ? "#cbd5e1" : "#475569",
                                minWidth: "auto",
                                px: 1,
                                py: 0.5,
                                "&:hover": {
                                  backgroundColor: "transparent",
                                  color: "#14b8a6",
                                },
                              }}
                            >
                              Edit
                            </Button>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteAuthorizedSigner(signer, rowIndex)}
                              sx={{
                                color: "#ef4444",
                                p: 0.5,
                                "&:hover": {
                                  backgroundColor: "rgba(239,68,68,0.08)",
                                },
                              }}
                            >
                              <DeleteOutlineOutlined fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {!hasAuthorizedSigners && (
                    <Typography
                      sx={{
                        fontSize: "0.85rem",
                        color: isDark ? "#64748b" : "#94a3b8",
                        mb: 3,
                      }}
                    >
                      No authorized signers added yet.
                    </Typography>
                  )}
                </>
              );
            }}
          />
        </Grid>

        <div style={{ height: "30px" }}></div>

        {/* ── Specializations & Services ── */}
        <Typography sx={sectionTitleSx}>Specializations & Services</Typography>
        <Divider sx={dividerSx} />

        <Typography component="label" sx={labelSx}>
          Type of Specialization(s) *
        </Typography>
        <Grid container spacing={2.5} mb={3}>
          <Grid item xs={12} sm={4}>
            <CustomDropdownField
              control={control}
              name={"specialization"}
              options={
                practiceSpecializationList
                  ? practiceSpecializationList.map((option) => ({
                    value: option.keyName,
                    label: option.displayValue,
                  }))
                  : []
              }
              placeholderText="Type of Specialization(s)"
              rules={{ required: "Type of Specialization(s) is required." }}
              sortOptions={false}
            />
          </Grid>
          <Grid item xs={12} sm={6}></Grid>
          {watch("specialization") !== undefined && watch("specialization") !== null && watch("specialization") !== "" && watch("specialization") !== "Surgical Only" && (<>
            <Grid item xs={12} sm={4}>
              <Typography component="label" sx={labelSx}>
                Wound Care Focus *
              </Typography>
              <CustomDropdownField
                control={control}
                name={`practiceRequestInfo.${"woundCareFocus"}`}
                options={
                  woundCareOptions
                    ? woundCareOptions.map((option) => ({
                      value: option.keyName,
                      label: option.displayValue,
                    }))
                    : []
                }
                placeholderText="Wound Care Focus"
                rules={{ required: "Wound Care Focus is required." }}
                sortOptions={false}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography component="label" sx={labelSx}>
                Average Wounds Treated Per Month *
              </Typography>
              <CustomNumberField control={control} controllerName={watch("practiceRequestInfo.woundCareFocus") === "Not Available" ? "" : `practiceRequestInfo.${"averageWoundsTreatedPerMonth"}`}
                placeholder="Average Wounds Treated Per Month" isDisabled={watch("practiceRequestInfo.woundCareFocus") === "Not Available"} isEmpty={watch("practiceRequestInfo.woundCareFocus") === "Not Available"}
                rules={
                  watch("practiceRequestInfo.woundCareFocus") === "Not Available" ? {} :
                    {
                      required: "Average Wounds Treated Per Month is required.",
                      pattern: {
                        value: /^[0-9]+$/,
                        message: "Please enter a valid number.",
                      },
                    }}
              />
            </Grid>
            <Grid item md={4}></Grid>
          </>)}

        </Grid>

        <Typography component="label" sx={labelSx}>
          What areas of care would you like to focus on? *
        </Typography>
        <Box sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          mt: 1,
          mb: 3,
          "& .MuiGrid-container": {
            flexWrap: "nowrap"
          },
          "& .MuiGrid-item": {
            flexGrow: 1,
            maxWidth: "none !important",
            flexBasis: "0 !important",
            width: "auto !important"
          }
        }}>
          <Box
            onClick={() => {
              const allKeys = productCategoryOptions.map((o) => o.value);
              setValue(
                "practiceRequestInfo.interestedProductCategory",
                allCareAreasSelected ? [] : allKeys,
                { shouldValidate: true }
              );
              setTimeout(() => handleProductCategoryChange(), 0);
            }}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              alignSelf: "flex-start",
              gap: 1,
              px: 1.5,
              py: 0.8,
              borderRadius: 1.5,
              cursor: "pointer",
              userSelect: "none",
              border: `1.5px solid ${allCareAreasSelected
                ? "rgba(20,184,166,0.5)"
                : isDark
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(0,0,0,0.08)"
                }`,
              backgroundColor: allCareAreasSelected
                ? isDark
                  ? "rgba(20,184,166,0.06)"
                  : "rgba(20,184,166,0.04)"
                : isDark
                  ? "rgba(255,255,255,0.02)"
                  : "rgba(0,0,0,0.015)",
              transition: "all 0.15s ease",
              "&:hover": {
                borderColor: allCareAreasSelected
                  ? "#14b8a6"
                  : isDark
                    ? "rgba(255,255,255,0.16)"
                    : "rgba(0,0,0,0.16)",
              },
            }}
          >
            <Checkbox
              checked={allCareAreasSelected}
              disableRipple
              size="small"
              sx={{
                color: isDark ? "#475569" : "#94a3b8",
                "&.Mui-checked": { color: "#14b8a6" },
                p: 0,
              }}
              tabIndex={-1}
            />
            <Typography
              sx={{
                fontSize: "0.82rem",
                fontWeight: 600,
                color: isDark ? "#e2e8f0" : "#1e293b",
                lineHeight: 1.3,
              }}
            >
              Select All
            </Typography>
          </Box>

          <CustomCheckboxGroup
            control={control}
            controllerName="practiceRequestInfo.interestedProductCategory"
            options={productCategoryOptions}
            rules={{ required: "At least one area of care must be selected." }}
            onChange={handleProductCategoryChange}
            checkboxSx={checkboxSx}
            radioLabelSx={radioLabelSx}
            cardMode
          />
        </Box>

        {(watchedCategories || []).includes(PRODUCT_CATEGORY.CONSERVATIVE) && (
            <Box mb={3}>
              <Typography component="label" sx={labelSx}>
                Date of Service Preference *
              </Typography>
              <CustomRadioButton
                control={control}
                radioSx={radioSx}
                radioLabelSx={radioLabelSx}
                controllerName="dosPreference"
                options={preferenceOptions}
                rules={{
                  required: "Date of Service Preference is required.",
                }}
              />
            </Box>
          )}

        <Typography component="label" sx={labelSx}>
          Place(s) of Service *
        </Typography>
        <Grid container spacing={2.5} mb={3}>
          <Grid item xs={12} sm={12}>
            <CustomCheckboxGroup control={control} controllerName={`practiceRequestInfo.${"placesOfService"}`}
              options={
                placeOfService
                  ? placeOfService.map((option) => ({
                    value: option.keyName,
                    label: option.displayValue,
                  }))
                  : []
              }
              rules={{
                required: "Place(s) of Service is required.",
              }}
              checkboxSx={checkboxSx}
              radioLabelSx={radioLabelSx}
            />
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Divider
            orientation="horizontal"
            variant="middle"
            flexItem
            sx={{ padding: 0, margin: "20px 0" }}
          />
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <Typography component="label" sx={labelSx}>
                Representative or Distributor Name {noRepChecked ? "" : "*"}
              </Typography>
              <CustomTextField
                control={control}
                controllerName={`practiceRequestInfo.${"repOrDistributorName"}`}
                placeholder="Enter the name of the representative or distributor"
                isDisabled={noRepChecked}
                rules={
                  noRepChecked
                    ? {}
                    : { required: "Name of the Representative or Distributor is required." }
                }
              />
            </Grid>
          </Grid>
          <Grid item xs={12} mt={1}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={noRepChecked}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setNoRepChecked(checked);
                    if (checked) {
                      unregister("practiceRequestInfo.repOrDistributorName");
                      setValue("practiceRequestInfo.repOrDistributorName", "No Representative or Distributor provided.");
                      clearErrors("practiceRequestInfo.repOrDistributorName");
                    } else {
                      setValue("practiceRequestInfo.repOrDistributorName", "");
                    }
                  }}
                  sx={{
                    color: isDark ? "#64748b" : "#94a3b8",
                    "&.Mui-checked": { color: "#14b8a6" },
                    padding: "4px 8px",
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontSize: "0.82rem",
                    color: isDark ? "#cbd5e1" : "#475569",
                  }}
                >
                  I don't have a representative or distributor
                </Typography>
              }
            />
          </Grid>
          <div style={{ height: "20px" }}></div>
          <Grid container spacing={2.5} mb={3}>
            <Grid item xs={12} sm={12}>
              <Typography component="label" sx={labelSx}>
                Additional Notes
              </Typography>
              <CustomTextField multiline control={control} controllerName={`practiceRequestInfo.${"anyQuestions"}`}
                rows={4} maxRows={4}
                placeholder="Please add any notes or specific requests that will help us serve you properly."
              />
            </Grid>
          </Grid>
        </Grid>
        <div style={{ height: "30px" }}></div>
        {/* <Header title="Insurance Payer Mix" subtitle="" /> */}
        <Grid container spacing={2}>
          {/* <Grid item md={4} xs={12}>
            <CustomDropdownField label="#1 Insurance Payer" name={`practiceRequestInfo.${"insurancePayer1"}`}
              options={insurancePayerOptions} control={control}
              // rules={{ required: "#1 Insurance Payer is required." }}
            />
            <p style={{ fontSize: "small" }}>As a percentage of volume.</p>
          </Grid>
          <Grid item md={4} xs={12}>
            <CustomDropdownField label="#2 Insurance Payer" name={`practiceRequestInfo.${"insurancePayer2"}`}
              options={insurancePayerOptions} control={control}
              // rules={{ required: "#2 Insurance Payer is required." }}
            />
            <p style={{ fontSize: "small" }}>As a percentage of volume.</p>
          </Grid>
          <Grid item md={4} xs={12}>
            <CustomDropdownField label="#3 Insurance Payer" name={`practiceRequestInfo.${"insurancePayer3"}`}
              options={insurancePayerOptions} control={control}
              // rules={{ required: "#3 Insurance Payer is required." }}
            />
            <p style={{ fontSize: "small" }}>As a percentage of volume.</p>
          </Grid> */}
          <Grid item md={12}>
            <p style={{ textAlign: "justify" }}>
              It is crucial to provide all the requested information as it
              directly impacts our ability to fulfill your needs effectively.
              Without the necessary details, we may encounter delays or
              limitations in providing you with the best possible service. Your
              cooperation in furnishing complete and accurate information is
              vital to ensuring a smooth and efficient process. Thank you for
              your attention to this matter.
            </p>
          </Grid>
        </Grid>
      </div>
    ),
    2: (
      <>
        <Header title="Business Associate Agreement" subtitle="" />
        <Grid container spacing={2}>
          <Grid item md={12}>
            <p style={{ textAlign: "justify" }}>
              This Business Associate Agreement ("Agreement") is by and between
              V3 Biomedical, LLC., a Delaware LLC having its principal place of
              business in Frisco, Texas ("V3"), and the Customer ("Customer")
              named in the Provider Registration Form under the applicable
              Services Agreement (as defined below The parties desire through
              this Agreement to amend the applicable Services Agreement
              consistent with the requirements of the Health Insurance
              Portability and Accountability Act of 1996, as it may be amended
              from time to time ("HIPAA"), including the regulatory revisions
              implemented pursuant to the Health Information Technology for
              Economic and Clinical Health Act (the "HITECH Act" This Agreement
              becomes effective on the date that this Agreement is signed by the
              last of the two parties to sign it (as indicated by the date
              associated with that party's signature) ("Effective Date" This
              Agreement replaces any earlier business associate agreement(s)
              entered into between the parties.
            </p>
            <p style={{ textAlign: "justify" }}>
              WHEREAS, Customer and V3 are parties to a services & support, or
              similar agreement or "terms of use" (collectively, the "Services
              Agreement," as further defined below) pursuant to which V3
              provides to Customer, through hosted software and other
              professional management services, access to and use of certain
              workflow management software tools as well as ongoing support and
              customer service, as ordered by Customer from time to time under
              the Services Agreement (the "Services," as further defined below);
            </p>
            <p style={{ textAlign: "justify" }}>
              WHEREAS, the parties desire to ensure that their respective rights
              and responsibilities under the Services Agreement reflect
              applicable federal statutory and regulatory requirements relating
              to the access, use, and disclosure of health information,
              including, without limitation, the Standards for Privacy of
              Individually Identifiable Health Information, and the Security
              Standards, collectively codified at 45 CFR Parts 160, 162, and 164
              (respectively the "Privacy Standards" and "Security Standards")
              under HIPAA;
            </p>
            <p style={{ textAlign: "justify" }}>
              WHEREAS, because Customer is a Covered Entity or Business
              Associate under HIPAA, the Privacy Standards and Security
              Standards require Customer to obtain adequate written assurances
              from contractors or subcontractors that create, receive, access,
              maintain, use, or disclose PHI for or on behalf of such Customer:
              and
            </p>
            <p style={{ textAlign: "justify" }}>
              WHEREAS, the online Services offered by V3 may be used by Customer
              to store certain PHI (though typically not electronic medical
              records or Designated Record Sets): and WHEREAS, the Customer
              wishes to have V3 manage all aspects of their service needs
              relating to acute and chronic wound care products. This includes
              the preferred utilization of "Patientflows," a HIPAA secure
              workflow management system used to streamline processes for
              products and services involving PHI; and
            </p>
            <p style={{ textAlign: "justify" }}>
              WHEREAS, V3 and Customer agree to the business associate terms set
              forth below in order to facilitate Customer's access and
              transmission of information to and from the Patientflows-hosted
              systems provided as part of the Services, as authorized by, and
              under certain other conditions described in the Services
              Agreement.
            </p>
            <p style={{ textAlign: "justify" }}>
              NOW, THEREFORE, for good and valuable consideration, the receipt
              and sufficiency of which is hereby acknowledged, the parties
              hereby amend their Services Agreement by agreement to the
              following:
            </p>
            <p style={{ textAlign: "left" }}>
              <b>1. DEFINITIONS</b>
            </p>
            <p style={{ textAlign: "justify" }}>
              (a) General. Capitalized terms used in this Agreement and not
              otherwise defined herein shall have the same meanings as defined
              in the Privacy Standards or Security Standards and corresponding
              official materials published, issued, or promulgated by the
              Secretary of the Department of Health and Human Services.
              "Protected Health Information" (or "PHI") shall have the same
              meaning as the term "protected health information" in 45 CFR §
              160.103, limited to the information actually received by V3 from
              or on behalf of the Covered Entity in connection with the Services
              Agreement.
            </p>
            <p style={{ textAlign: "justify" }}>
              (b) Specific Definitions. As used herein:
            </p>
            <p style={{ textAlign: "justify" }}>
              "Services" means specifically the instance of the V3 web
              application, identified as "Patienflows by Patientflows LLC,"
              "V3," or similar services in the Customer's signature block, along
              with Patientflows mobile software applications used by the
              Customer therewith. For clarification, such Services do not
              include
            </p>
            <table style={{ width: "100%", textAlign: "justify" }}>
              <tbody>
                <tr>
                  <td style={{ width: "3%" }}></td>
                  <td style={{ width: "97%" }}>
                    (i) any general obligation to supervise, oversee, or consult
                    with Customer for the purposes of advising Customer on or
                    ensuring Customer's compliance with HIPAA, the HITECH Act,
                    and HIPAA Regulations, <br />
                    <br />
                  </td>
                </tr>
                <tr>
                  <td style={{ width: "3%" }}></td>
                  <td style={{ width: "97%" }}>
                    (ii) any services offered by any third party, including V3
                    partners, <br />
                    <br />
                  </td>
                </tr>
                <tr>
                  <td style={{ width: "3%" }}></td>
                  <td style={{ width: "97%" }}>
                    (iii) any services offered in the Patientflows Marketplace
                    by V3, <br />
                    <br />
                  </td>
                </tr>
                <tr>
                  <td style={{ width: "3%" }}></td>
                  <td style={{ width: "97%" }}>
                    (iv) web and mobile applications, nor <br />
                    <br />
                  </td>
                </tr>
                <tr>
                  <td style={{ width: "3%" }}></td>
                  <td style={{ width: "97%" }}>
                    (v) the email, SMS, and VolP functionalities within the
                    Patientflows web or mobile applications.
                  </td>
                </tr>
              </tbody>
            </table>
            <p style={{ textAlign: "justify" }}>
              "Services Agreement" means the current or any future agreement
              between Customer and V3, under which V3 provides the Services to
              Customer, which involve the use or disclosure of Protected Health
              Information, particularly the "terms of use" or any subscription
              or partner agreements, as such agreement(s) may be amended,
              modified, or renamed in the future.
            </p>
            <p style={{ textAlign: "left" }}>
              <b>2. OBLIGATIONS AND ACTIVITIES OF V3 BIOMED</b>
            </p>
            <p style={{ textAlign: "justify" }}>
              (a) Use and Disclosure To the extent (if any) that V3 creates,
              transmits, maintains, or receives any PHI on behalf of Customer,
              including any Electronic PHI, V3 agrees to:
            </p>
            <table style={{ width: "100%", textAlign: "justify" }}>
              <tbody>
                <tr>
                  <td style={{ width: "3%" }}></td>
                  <td style={{ width: "97%" }}>
                    (i) maintain the privacy and security of such PHI and not
                    use or disclose PHI other than as permitted or required to
                    satisfy its obligations under the Services Agreement, as
                    permitted herein, or as required by law; <br />
                    <br />
                  </td>
                </tr>
                <tr>
                  <td style={{ width: "3%" }}></td>
                  <td style={{ width: "97%" }}>
                    (ii) use appropriate safeguards, consistent with the
                    requirements of Subparts B and C of 45 CFR $164.504(e)(2){" "}
                    <br />
                    <br />
                  </td>
                </tr>
                <tr>
                  <td style={{ width: "3%" }}></td>
                  <td style={{ width: "97%" }}>
                    (iii) (with respect to Electronic PHI to prevent the use or
                    disclosure of the PHI other than as permitted under this
                    Addendum: <br />
                    <br />
                  </td>
                </tr>
                <tr>
                  <td style={{ width: "3%" }}></td>
                  <td style={{ width: "97%" }}>
                    (iv) implement or maintain administrative, physical, and
                    technical safeguards that reasonably and appropriately
                    protect the confidentiality, integrity, and availability of
                    Electronic PHI; and <br />
                    <br />
                  </td>
                </tr>
                <tr>
                  <td style={{ width: "3%" }}></td>
                  <td style={{ width: "97%" }}>
                    (v) to promptly report to Customer any use or disclosure of
                    PHI not permitted by this Addendum of which V3 becomes aware
                    (including Breaches of Unsecured PHI as required at 45 CFR §
                    164.410) and any Security Incident that V3 becomes aware;
                    provided, however, that the parties acknowledge and agree
                    that this Section 2(a) constitutes notice by V3 to Customer
                    of the ongoing existence and occurrence or attempts of
                    Unsuccessful Security Incidents for which no additional
                    notice to Customer shall be required. "Unsuccessful Security
                    Incidents" means, without limitation, pings and other
                    broadcast attacks on V3's firewall(s), port scans,
                    unsuccessful log-on attempts, denial of service attacks, and
                    any combination of the above, so long as no such incident
                    results in unauthorized access, use or disclosure of PHI.
                  </td>
                </tr>
              </tbody>
            </table>
            <p style={{ textAlign: "justify" }}>
              (b) Agents. V3 shall obtain and maintain an agreement with each
              agent or subcontractor that has or will have access to PHI. That
              agreement will require each such agent or subcontractor to be
              bound by the restrictions, terms, and conditions that are at least
              as stringent as those that apply to V3 with respect to such PHI.
              Further, each agent or subcontractor will agree to report to V3
              any instances in which it is aware of a violation of the agreement
              with respect to PHI.
            </p>
            <p style={{ textAlign: "justify" }}>
              (c) Access to Designated Record Sets. To the extent (if any) that
              V3 possesses and maintains a Designated Record Set for Customer,
              V3 agrees to:
            </p>
            <table style={{ width: "100%", textAlign: "justify" }}>
              <tbody>
                <tr>
                  <td style={{ width: "3%" }}></td>
                  <td style={{ width: "97%" }}>
                    (i) provide access, at the request of Customer, and in the
                    time and manner mutually agreed between Wilco and Customer,
                    to PHI in a Designated Record Set, to Customer or, as
                    directed by Customer, to an Individual in order to satisfy
                    Customer's obligations under 45 CFR § 164.524; and <br />
                    <br />
                  </td>
                </tr>
                <tr>
                  <td style={{ width: "3%" }}></td>
                  <td style={{ width: "97%" }}>
                    (ii) to make any amendment(s) to PHI in a Designated Record
                    Set as directed or agreed to by the Customer pursuant to 45
                    CFR § 164.526, and in the time and manner mutually agreed
                    between V3 and Customer (provided that the amendment of an
                    Individual's PHI and all decisions related thereto shall be
                    the sole responsibility of Customer).
                  </td>
                </tr>
              </tbody>
            </table>
            <p style={{ textAlign: "justify" }}>
              (d) Accounting. V3 agrees to make available to Customer
              information regarding disclosures made by V3 for which an
              accounting is required under 45 CFR § 164,528 so Customer can meet
              its requirements to provide an accounting to an individual in
              accordance with 45 CFR § 164,528.
            </p>
            <p style={{ textAlign: "justify" }}>
              (e) Access to Books and Records. V3 agrees to make its internal
              practices, books, and records relating to the use and disclosure
              of PHI available to the Secretary for purposes of determining
              compliance with the HIPAA Rules.
            </p>
            <p style={{ textAlign: "left" }}>
              <b>3. PERMITTED USES AND DISCLOSURES BY V3 BIOMED</b>
            </p>
            <p style={{ textAlign: "justify" }}>
              (a) Services Agreement Except as otherwise limited by this
              Agreement, V3 may use or disclose PHI as necessary to perform the
              Services for Customer as specified in the Services Agreement,
              provided that such use or disclosure would not violate the Privacy
              Rule if done by Customer and complies with the principle of
              "minimum necessary use and disclosure" consistent with 45 CFR §
              164.514(d).
            </p>
            <p style={{ textAlign: "justify" }}>
              (b) Disclosure for Administration of V3. Except as otherwise
              limited by this Agreement, V3 may disclose PHI for the proper
              management and administration of V3, provided that
            </p>
            <table style={{ width: "100%", textAlign: "justify" }}>
              <tbody>
                <tr>
                  <td style={{ width: "3%" }}></td>
                  <td style={{ width: "97%" }}>
                    (i) disclosures are required by law or <br />
                    <br />
                  </td>
                </tr>
                <tr>
                  <td style={{ width: "3%" }}></td>
                  <td style={{ width: "97%" }}>
                    (ii)V3 obtains reasonable assurances from the person to whom
                    the information is disclosed that it will remain
                    confidential and used or further disclosed only as Required
                    by Law or for the purpose for which it was disclosed to the
                    person. The person notifies V3 of any instances of which it
                    is aware in which the confidentiality of the information has
                    been breached
                  </td>
                </tr>
              </tbody>
            </table>
            <p style={{ textAlign: "justify" }}>
              (c) Reporting Violations. V3 may use PHI to report violations of
              law to appropriate Federal and State authorities, consistent with
              45 CFR § 164.502(1).
            </p>
            <p style={{ textAlign: "left" }}>
              <b>4. OBLIGATIONS OF CUSTOMER</b>
            </p>
            <p style={{ textAlign: "justify" }}>
              (a) Limitations in Notice of Privacy Practices. Customer shall
              notify V3 of any limitation(s) in the notice of privacy practices
              of Customer under 45 CFR § 164,520 to the extent that such
              limitation may affect Patientflows use or disclosure of PHI.
              Granted, however, the Customer shall not agree to any restriction
              requests or place any restrictions in any notice of privacy
              practices that would cause V3 to violate this Addendum or any
              applicable law.
            </p>
            <p style={{ textAlign: "justify" }}>
              (b) Restrictions to the Use or Disclosure of PHI. The customer
              shall notify V3 of any restriction regarding the use or disclosure
              of PHI that the Customer has agreed to in accordance with 45 CFR 5
              164.522, to the extent that such restriction may affect
              Patientflows' use or disclosure of PHI.
            </p>
            <p style={{ textAlign: "justify" }}>
              (c) Permissible Use Requests. Except for the permitted uses set
              forth in Section 3, Customer will not request V3 to use or
              disclose PHI in any manner that would not be permissible under
              Subpart E of 45 CFR $164,504 if done by Customer.
            </p>
            <p style={{ textAlign: "justify" }}>
              (d) Access Requests from Data Subjects. V3 will notify the
              Customer of any request from a data subject for access to, or
              correction, erasure, blocking, or amendment of, the data subject's
              PHI if legally permitted. V3 will ask the data subject to submit
              its request to the Customer. The customer is responsible for
              responding to the request.
            </p>
            <p style={{ textAlign: "left" }}>
              <b>5. TERM AND TERMINATION</b>
            </p>
            <p style={{ textAlign: "justify" }}>
              (a) Term and Termination The term of this Agreement shall begin on
              the Effective Date. It shall terminate on the date that the
              Services Agreement expires or is otherwise terminated for any
              reason or the date Customer terminates this Agreement for cause as
              authorized in this Section, or the date either party terminates
              this Agreement for convenience, whichever is earliest.
            </p>
            <p style={{ textAlign: "justify" }}>
              (b) Termination for Cause. Upon Customer's knowledge of a material
              breach of this Agreement by V3, Customer shall notify V3 of the
              breach in writing and shall provide an opportunity for V3 to cure
              the breach or end the violation within thirty (30) business days
              of such notification: provided, that if V3 fails to cure the
              breach or end the violation within such time period to the
              satisfaction of Customer, Customer shall have the right to
              immediately terminate this Agreement and the Services Agreement
              upon written notice to V3. In the event that termination of this
              Agreement is not feasible as mutually agreed to by V3 and
              Customer, V3 hereby acknowledges that Customer shall have the
              right to report the breach to the Secretary.
            </p>
            <p style={{ textAlign: "justify" }}>
              (c) Termination for Convenience. Either party has the right to
              terminate this Agreement for any reason, and without terminating
              the Services Agreement, upon 60 days prior written notice to the
              other party. Where Customer terminates this Agreement for
              convenience, the Customer represents and warrants that it has
              removed all PHI from the Services.
            </p>
            <p style={{ textAlign: "justify" }}>
              (d) Effect of Termination. Following the termination or expiration
              of this Agreement for any reason, V3 shall comply with the
              request(s) of the Customer that V3 returns or destroy all PHI
              received from Customer or received by V3 on behalf of Customer.
              This provision shall also apply to PHI that is in the possession
              of subcontractors or agents of V3. V3 shall retain no copies of
              the PHI. Notwithstanding the foregoing, in the event that V3
              determines that returning or destroying the PHI is infeasible, V3
              shall provide to Customer notification of the conditions that make
              return or destruction infeasible. Further, in consideration of the
              fact that V3 maintains a procedure for replicating or backing up
              Customer data to help ensure the integrity and availability of
              PHI, the parties agree that it is infeasible for V3 to return or
              destroy PHI that is contained in any and all such data backups or
              data replications. Where return or destruction of PHI is
              infeasible, V3 shall extend the protections of this Addendum to
              such PHI and limit further uses and disclosures of such PHI to
              those purposes that make the return or destruction infeasible for
              so long as V3 maintains such PHI.
            </p>
            <p style={{ textAlign: "justify" }}>
              (e) Survival. The obligations of V3 under this Section shall
              survive the termination of this Agreement.
            </p>
            <p style={{ textAlign: "left" }}>
              <b>6. MISCELLANEOUS</b>
            </p>
            <p style={{ textAlign: "justify" }}>
              (a) Regulatory References. A reference in this Agreement to a
              section in the HIPAA Rules means the section is in effect or as
              amended or modified from time to time.
            </p>
            <p style={{ textAlign: "justify" }}>
              (b) Amendment No alteration, amendment, or modification of the
              terms of this Addendum shall be valid or effective unless in
              writing and signed by Wilco and Customer.
            </p>
            <p style={{ textAlign: "justify" }}>
              (c) Miscellaneous The terms of this Agreement are hereby
              incorporated into the Services Agreement. In the event of a
              conflict between the terms of this Agreement and the terms of the
              Services Agreement, the terms of this Agreement will prevail. Any
              ambiguity in this Agreement shall be interpreted to permit
              compliance with the HIPAA Rules.
            </p>
          </Grid>
          <Grid item md={12}>
            <div style={{ fontSize: "medium" }}>Customer Signature</div>
          </Grid>
          <Grid item md={6} xs={12}>
            <SignaturePad
              onSignatureChange={handleSignatureChange}
              name={`practiceRequestInfo.${"customerSignature"}`}
              textName={`practiceRequestInfo.${"customerName"}`}
              control={control}
              validationRules={signaturePadValidations}
            />
          </Grid>
          {/* <Grid item md={12}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
              }}
            >
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={process.env.REACT_APP_RECAPTCHA_SITEKEY + ""}
                onChange={captchaOnChange}
                onExpired={() => {
                  setIsRecaptchaValid(false);
                  recaptchaRef.current.reset();
                }}
              />
            </div>
          </Grid> */}
        </Grid>
      </>
    ),
  };

  return (
    <>
      <PracticeLocationPopup
        onClose={clickClosePracticesPopup}
        open={showPracticesPopup}
        addPractice={addPractice}
        practiceLocations={getValues("practiceLocations") || []}
        billingAddressPanel={showBillingAddressOption}
        practiceLocationObject={locationItemObject}
      />
      <ProviderPopup
        onClose={clickCloseProviderPopup}
        open={showProviderPopup}
        addProvider={addProvider}
        onChange={handleProviderChange}
        practiceProviderObject={providerItemObject}
        billingType={getValues("billingType")}
      />
      {showAuthorizedSignerPopup &&
        <AuthorizedSignerPopup
          onClose={clickCloseAuthorizedSignerPopup}
          open={showAuthorizedSignerPopup}
          addAuthorizedSigner={addSigner}
          authorizedSignerData={authorizedSignerData}
        />
      }
      <Box sx={{
        margin: {
          xs: "20px 10px",
          sm: "20px",
        },
      }}>
        <Header title="Practice Registration" />
        <Card
          sx={{
            width: { xs: '100%', sm: '100%', md: '100%' },
            maxWidth: 'none',
            mx: 'auto',
            px: 0,
          }}
        >
          <form ref={formRef} onSubmit={handleSubmit(onSubmit, onError)}>
            <FormGroup>
              {stepContent[currentStep]}
              <Grid container justifyContent="flex-end" sx={{ mt: 1, flexWrap: "wrap" }} spacing={1} >
                {/* <Grid item xs={12} sm="auto">
                  {currentStep > 1 && (
                    <Button onClick={handleBackClick} sx={{ mr: 1 }} startIcon={<ArrowBack />} fullWidth >
                      Back
                    </Button>
                  )}
                </Grid> */}
                <Grid item xs={12} sm="auto">
                  {/* <Button sx={{ mr: 1 }} disabled={currentStep >= totalSteps && !isRecaptchaValid} type="submit" fullWidth
                    startIcon={ currentStep >= totalSteps ? <Save /> : <ArrowForward /> } >
                    {currentStep >= totalSteps ? "Submit" : "Next"}
                  </Button> */}
                  {/* <Grid item md={12} mb={2}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: { xs: "center", md: "flex-end" },
                      justifyContent: { xs: "center", md: "flex-end" },
                    }}
                  >

                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", }} >
                      <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={process.env.REACT_APP_RECAPTCHA_SITEKEY + ""}
                        onChange={captchaOnChange}
                        onExpired={() => {
                          setIsRecaptchaValid(false);
                          recaptchaRef.current.reset();
                        }}
                      />
                    </div>
                  </Grid> */}
                  {/* <Button sx={{ mr: 1 }} disabled={!isRecaptchaValid} type="submit" fullWidth
                    startIcon={<Save />} >
                    Submit
                  </Button> */}
                  <Box sx={footerSx}>
                    <Box sx={{ display: "flex", gap: 1.5 }}>
                      <Button
                        fullWidth
                        variant="contained"
                        type='submit'
                        startIcon={
                          <AddCircleOutline sx={{ fontSize: "1rem !important" }} />
                        }
                        sx={primaryBtnSx}
                      >
                        Submit
                      </Button>

                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<CancelIcon sx={{ fontSize: "1rem !important" }} />}
                        onClick={handleCancel}
                        sx={cancelBtnSx}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </FormGroup>
          </form>
        </Card>
      </Box>
    </>
  );
}

export default PracticeRegistration;